import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, UserPlus } from "lucide-react";
import { api, apiErr } from "../../api";
import { Button, Spinner } from "../../Primitives";
import { ROLE_OPTIONS } from "./roles";

export function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4" role="dialog" aria-modal="true" aria-label={title} data-testid={testid}>
      <motion.div className="absolute inset-0 bg-black/65 backdrop-blur-[3px]" onClick={onClose} initial={{ opacity: 0 }} animate={{ opacity: 1 }} />
      <motion.div className="relative mia-panel w-full sm:max-w-[460px] p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] max-h-[92dvh] overflow-y-auto mia-scroll !rounded-b-none sm:!rounded-[var(--radius)] !rounded-t-[14px]" initial={{ opacity: 0, y: 24, scale: 1 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-[15px] font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Fechar" data-testid={`${testid}-close`}><X size={15} /></Button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export default function UserDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", role: "analyst" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.name.trim().length < 2) return setErr("Informe o nome.");
    if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) return setErr("Informe um e-mail válido.");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/users", { ...f, email: f.email.trim() });
      onCreated(data);
    } catch (e2) { setErr(apiErr(e2)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Novo acesso" onClose={onClose} testid="user-dialog">
      <form onSubmit={submit} className="grid gap-3.5 text-[13px]" data-testid="user-form">
        <label className="block space-y-1"><span className="block ops-label">Nome</span>
          <input className="mia-input" value={f.name} onChange={set("name")} maxLength={80} autoFocus data-testid="user-form-name" /></label>
        <label className="block space-y-1"><span className="block ops-label">E-mail</span>
          <input className="mia-input" type="email" value={f.email} onChange={set("email")} maxLength={254} data-testid="user-form-email" /></label>
        <fieldset className="space-y-1.5">
          <legend className="ops-label mb-1">Nível de acesso</legend>
          {ROLE_OPTIONS.map((r) => (
            <label key={r.value} className={`flex gap-2.5 items-start p-2.5 rounded-[var(--radius-sm)] border cursor-pointer transition-colors ${f.role === r.value ? "border-[var(--accent-line)] bg-[var(--accent-weak)]" : "border-[var(--border)] hover:border-[var(--border-strong)]"}`}>
              <input type="radio" name="role" value={r.value} checked={f.role === r.value} onChange={set("role")} className="mt-0.5 accent-[var(--accent)]" data-testid={`user-form-role-${r.value}`} />
              <span><span className="block font-medium">{r.label}</span><span className="ops-help">{r.help}</span></span>
            </label>
          ))}
        </fieldset>
        <p className="ops-help">Será gerado um link de convite seguro (uso único, expira em 48 h) para a pessoa definir a própria senha. Nenhuma senha é criada por você.</p>
        {err && <p role="alert" data-testid="user-form-error" style={{ color: "var(--bad)" }}>{err}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" size="sm" onClick={onClose} data-testid="user-form-cancel">Cancelar</Button>
          <Button type="submit" size="sm" disabled={busy} data-testid="user-form-submit">{busy ? <Spinner size={12} /> : <><UserPlus size={13} /> Criar e gerar convite</>}</Button>
        </div>
      </form>
    </Modal>
  );
}
